/**
 * DEMO MODE (rev 12) — bypass the login screen for UI review while the real
 * identity stack is unfinished.
 *
 * WHY THIS EXISTS
 *   The deployed frontend cannot authenticate: `apps/web/src/lib/auth.ts`
 *   POSTs to `/api/auth/login` (an Express route that is not deployed), the
 *   Supabase Edge Function requires a `tenant_id` the frontend never sends,
 *   and Cloudflare Pages serves static files with an SPA fallback for every
 *   path. The result is a login screen that can never succeed, which blocks
 *   all UI review.
 *
 * WHY IT IS NOT "REMOVING THE LOGIN"
 *   Deleting the guard, or accepting any token, would undo the rev9–rev11
 *   security work and would create a permanent backdoor. This module instead
 *   does the narrow, honest thing:
 *
 *     • It seeds a LOCAL, clearly-labelled demo session so the UI renders.
 *     • It does NOT change the API. The server still requires a signed JWT and
 *       still denies by default. Demo mode can never become a server bypass,
 *       because it mints nothing the server accepts.
 *     • It is OFF unless `VITE_DEMO_MODE=1` is set at build time.
 *     • It is impossible to confuse with a real login: the user, tenant and
 *       banner are visibly synthetic.
 *
 * The consequence is stated plainly: in demo mode the app renders, but data
 * calls still fail closed, because the API is not deployed. That is the
 * correct outcome — a demo session must not become a way to read real records.
 */

/** Storage key shared with `lib/route-auth.ts`. */
const SESSION_KEY = "dikdoc_user";

/** Banner state, so the shell can render an unambiguous warning. */
const DEMO_FLAG_KEY = "dikdoc_demo_active";

/**
 * Is the app running WITHOUT a working identity stack?
 *
 * Rev27.2 — this used to be a pure build-time flag:
 *
 *     const raw = import.meta.env?.VITE_DEMO_MODE;
 *     return String(raw ?? "").trim() === "1";
 *
 * which made the "Ouvrir en mode démo" button on /login DEAD CODE in any
 * build without the flag: `installDemoSession()` returned false, the login
 * page re-rendered the same notice, and the user bounced between /login and /
 * forever. The button's own comment claimed it was a "runtime enable, so UI
 * review no longer depends on a rebuild" — it was not.
 *
 * The rule is now explicit and self-disabling:
 *
 *   • `VITE_DEMO_MODE=1`            → demo mode ON, whatever else is set.
 *   • `VITE_DEMO_MODE=0`            → demo mode OFF, whatever else is set.
 *   • unset + no `VITE_API_URL`     → demo mode ON. There is no API, so there
 *                                     is no identity to establish and nothing
 *                                     for a login to protect.
 *   • unset + `VITE_API_URL` set    → demo mode OFF. The API exists, so the
 *                                     real identity stack owns the gate.
 *
 * The last case is what makes this safe to ship: the moment the API and the
 * per-doctor 2FA identity are configured, demo mode switches itself off and
 * the login screen comes back. Nothing has to be remembered or un-set.
 *
 * DEMO MODE NEVER GRANTS API ACCESS. It writes a local, clearly-labelled
 * synthetic user and NO token. The API keeps denying by default. See
 * apps/api/src/middleware/auth-guard.ts — that is the real boundary.
 */
export function isDemoMode(): boolean {
  const raw = import.meta.env?.VITE_DEMO_MODE;
  const flag = String(raw ?? "").trim();
  if (flag === "1") return true;
  if (flag === "0") return false;
  // Unset: no API means no identity stack, so there is no login to enforce.
  const api = String(import.meta.env?.VITE_API_URL ?? "").trim();
  return api === "";
}

/** Minimal SHA-256 over a UTF-8 string, using WebCrypto. */
async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const DEMO_USER = {
  id: "demo-superadmin",
  email: "admin@medigroup.ma",
  name: "Demo (synthetic)",
  roles: ["admin", "doctor"],
  tenantId: "demo-tenant",
  tenantName: "Demo — données synthétiques",
} as const;

/**
 * Install the demo session if demo mode is on and no real session exists.
 *
 * A REAL session is never overwritten: if a genuine login has already stored
 * one, it is left alone.
 */
export function installDemoSession(options?: { force?: boolean }): boolean {
  // `force` is the explicit user action behind "Ouvrir en mode démo": a person
  // clicking the button is asking for the demo, so it must work even in a
  // build where demo mode is not the configured mode. It still mints no token
  // and still grants no API access.
  if (!isDemoMode() && !options?.force) return false;
  try {
    if (window.localStorage.getItem(SESSION_KEY)) return false; // real session wins
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(DEMO_USER));
    window.localStorage.setItem(DEMO_FLAG_KEY, "1");
    // No token is written. Deliberately: the API must keep denying.
    return true;
  } catch {
    return false;
  }
}

export function isDemoSession(): boolean {
  try {
    return window.localStorage.getItem(DEMO_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remove the demo session, restoring the login screen. */
export function clearDemoSession(): void {
  try {
    if (isDemoSession()) window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(DEMO_FLAG_KEY);
  } catch {
    /* storage unavailable */
  }
}
